import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import scorecardService from 'services/scorecardService';
import { getAccountScanPermissions } from './scanPermissionsSlice';

const initialState = {
    allScorecards: null,
    scorecardLoading: false,
    scorecardError: false,

    scorecardDetails: null,
    scorecardDigitalFootprints: null,
    scorecardDetailsLoading: false,
    scorecardDetailsError: false,

    hasFraudScorecardDetail: false,
    hasFraudScorecardLoading: false,

    hasSSLScorecardDetail: null,
    hasSSLScorecardLoading: false,

    hasDehashedScorecardDetail: null,
    hasDehashedScorecardLoading: false,

    industryBenchmark: [],
    industryBenchmarkLoading: false,

    hasDarkWebScorecardDetail: null,
    hasDarkWebScorecardLoading: false,

    scorecardPageableRequest: {
        data: {
            name: "",
            type: ["DOMAIN"],
            scoreLetter: ""
        },
        params: {
            page: 0,
            size: 6,
            sort: "totalScore",
            direction: "ASC"
        }
    },

    thirdPartyScanAssetLoading: false,
    currentStep: 0,
    createStatus: {
        message: null,
        error: false,
        code: null,
    },

    thirdPartyAddLoading: false,
    thirdPartyAddError: false,

    thirdPartyDeleteLoading: false,
    thirdPartyDeleteError: false,

    thirdPartyGetLoading: false,
    thirdPartyGetError: false,
    thirdPartyGetAll: [],

    thirdPartyPageableLoading: false,
    thirdPartyPageableError: false,
    thirdPartyPageableData: {
        content: [],
        totalElements: 0,
        totalPages: 0,
        pageable: {
            pageNumber: 0,
            pageSize: 10
        }
    },

    thirdPartyGetExternalLoading: false,
    thirdPartyGetExternalError: false,
    thirdPartyGetAllExternal: [],

    thirdPartyAddContactLoading: false,
    thirdPartyAddContactError: false,

    thirdPartyDeleteContactError: false,
    thirdPartyDeleteContactLoading: false,

    sendMailError: false,
    sendMailLoading: false,

    externalHistoryDetails: [],
    externalHistoryDetailsLoading: false,
    externalHistoryDetailsError: false,

    notRegisteredThirdPartyAssets: null,

    confirmationTemplateLoading: false,
    confirmationTemplate: [],
    confirmationTemplateSaveLoading: false,

    
  activeDomainState: null,
  activeTabState: null,
};

export const getScorecardPageable = createAsyncThunk(
    "scorecard/getScorecardPageable",
    async (data, { rejectWithValue }) => {
        try {
            const response = await scorecardService.getScorecardPageable(data);
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const getScorecardHistory = createAsyncThunk(
    "scorecard/getScorecardHistory",
    async (id, { rejectWithValue }) => {
        try {
            const response = await scorecardService.getScorecardHistory(id);
            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const getExternalHistory = createAsyncThunk(
    "scorecard/getExternalHistory",
    async (id, { rejectWithValue }) => {
        try {
            const response = await scorecardService.getExternalHistory();
            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const getDigitalFootprints = createAsyncThunk(
    "scorecard/getDigitalFootprints",
    async (parentDomainId, { rejectWithValue }) => {
        try {
            const response = await scorecardService.getDigitalFootprints(parentDomainId);
            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const hasFraudScorecard = createAsyncThunk(
    "scorecard/hasFraudScorecard",
    async (id, { rejectWithValue }) => {
        try {
            const response = await scorecardService.hasFraudScorecard(id);
            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const hasSSLScorecard = createAsyncThunk(
    "scorecard/hasSSLScorecard",
    async (id, { rejectWithValue }) => {
        try {
            const response = await scorecardService.hasSSLScorecard(id);
            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const hasDehashedScorecard = createAsyncThunk(
    "scorecard/hasDehashedScorecard",
    async (id, { rejectWithValue }) => {
        try {
            const response = await scorecardService.hasDehashedScorecard(id);
            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const hasDarkWebScorecard = createAsyncThunk(
    "scorecard/hasDarkWebScorecard",
    async (id, { rejectWithValue }) => {
        try {
            const response = await scorecardService.hasDarkWebScorecard(id);
            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const startThirdPartyDomainScan = createAsyncThunk("scorecard/startThirdPartyDomainScan", async (data, {
    rejectWithValue, dispatch
}) => {
    try {
        const response = await scorecardService.startThirdPartyDomainScan(data);
        return response;
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const getMultipleSelectedScorecards = createAsyncThunk("scorecard/getMultipleSelectedScorecards", async (_, {
    rejectWithValue, dispatch
}) => {
    try {
        const response = await scorecardService.getMultipleSelectedScorecards();
        return response;
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const addMultipleSelectedScorecards = createAsyncThunk("scorecard/addMultipleSelectedScorecards", async (data, {
    rejectWithValue, dispatch
}) => {
    try {
        const response = await scorecardService.addMultipleSelectedScorecards(data);
        if (response.status === 200) {
            dispatch(getAccountScanPermissions());
        }
        return response;
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const deleteMultipleSelectedScorecards = createAsyncThunk("scorecard/deleteMultipleSelectedScorecards", async (data, {
    rejectWithValue, dispatch
}) => {
    try {
        const response = await scorecardService.deleteMultipleSelectedScorecards(data);
        if (response.status === 200) {
            dispatch(getAccountScanPermissions());
        }
        return response;
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const getAllExternalScorecards = createAsyncThunk("scorecard/getAllExternalScorecards", async (_, {
    rejectWithValue, dispatch
}) => {
    try {
        const response = await scorecardService.getAllExternalScorecards();
        return response;
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const getThirdPartyAssessmentPageable = createAsyncThunk("scorecard/getThirdPartyAssessmentPageable", async (data, {
    rejectWithValue
}) => {
    try {
        const response = await scorecardService.getThirdPartyAssessmentPageable(
            data?.params, data?.data
        );
        return response.data;
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const addSelectedScorecardWithContact = createAsyncThunk("scorecard/addSelectedScorecardWithContact", async (data, {
    rejectWithValue, dispatch
}) => {
    try {
        const response = await scorecardService.addSelectedScorecardWithContact(data);
        return response;
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const deleteSelectedScorecardContact = createAsyncThunk(
    'scorecard/deleteSelectedScorecardContact',
    async ({ domainId, contactId }, { rejectWithValue }) => {
        try {
            const response = await scorecardService.deleteSelectedScorecardContact({ domainId, contactId });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const sendThirdPartyMail = createAsyncThunk(
    "scorecard/sendThirdPartyMail",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await scorecardService.sendThirdPartyMail(payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const sendInitialConfirmation = createAsyncThunk(
    "scorecard/sendInitialConfirmation",
    async ({ domainId }, { rejectWithValue }) => {
        try {
            const response = await scorecardService.sendInitialConfirmation(domainId);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const resendConfirmation = createAsyncThunk(
    "scorecard/resendConfirmation",
    async ({ domainId }, { rejectWithValue }) => {
        try {
            const response = await scorecardService.resendConfirmation(domainId);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const rescanDomain = createAsyncThunk(
    "scorecard/rescanDomain",
    async ({ domainId }, { rejectWithValue }) => {
        try {
            const response = await scorecardService.rescanDomain(domainId);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const getIndustryBenchmark = createAsyncThunk(
    'scorecard/getIndustryBenchmark',
    async (_, { rejectWithValue }) => {
        try {
            const response = await scorecardService.getIndustryBenchmark();
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const getConfirmationTemplate = createAsyncThunk(
    'scorecard/getConfirmationTemplate',
    async (_, { rejectWithValue }) => {
        try {
            const response = await scorecardService.getConfirmationTemplate();
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const saveConfirmationTemplate = createAsyncThunk(
    'scorecard/saveConfirmationTemplate',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await scorecardService.saveConfirmationTemplate(payload);
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const deleteConfirmationTemplate = createAsyncThunk(
    'scorecard/deleteConfirmationTemplate',
    async (_, { rejectWithValue }) => {
        try {
            await scorecardService.deleteConfirmationTemplate();
            return null;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);


export const scorecardSlice = createSlice({
    name: 'scorecardSlice',
    initialState,
    reducers: {
        setStep: (state, action) => {
            state.currentStep = action.payload;
        },
        resetScanStatus: (state) => {
            state.currentStep = 0;
            state.createStatus = {
                message: null,
                error: false,
                code: null,
            };
            state.thirdPartyScanAssetLoading = false;
        },
        resetNotRegisteredThirdPartyAssets: (state) => {
            state.notRegisteredThirdPartyAssets = null;
        },
        setActiveDomainWithTab: (state, action) => {
            state.activeDomainState = action.payload.domain;
            state.activeTabState = action.payload.tab;
        },
        clearActiveDomainState: (state) => {
            state.activeDomainState = null;
            state.activeTabState = null;
        },
        setActiveTab: (state, action) => {
            state.activeTabState = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getScorecardPageable.fulfilled, (state, action) => {
            if (action?.payload?.response?.status === 500 || action?.payload?.response?.status === 404 || action?.payload === undefined) {
                state.scorecardError = true;
                state.scorecardLoading = false;
            } else {
                state.allScorecards = action?.payload;
                state.scorecardLoading = false;
                state.scorecardError = false;
            }
        });
        builder.addCase(getScorecardPageable.pending, (state) => {
            state.scorecardLoading = true;
            state.scorecardError = false;
        });
        builder.addCase(getScorecardPageable.rejected, (state, action) => {
            state.scorecardLoading = false;
            state.scorecardError = true;
        });
        builder.addCase(getScorecardHistory.fulfilled, (state, action) => {
            if (action?.payload?.response?.status === 500 || action?.payload?.response?.status === 404 || action?.payload === undefined) {
                state.scorecardDetailsError = true;
                state.scorecardDetailsLoading = false;
            } else {
                state.scorecardDetails = action?.payload;
                state.scorecardDetailsLoading = false;
                state.scorecardDetailsError = false;
            }
        });
        builder.addCase(getScorecardHistory.pending, (state) => {
            state.scorecardDetailsLoading = true;
            state.scorecardDetailsError = false;
        });
        builder.addCase(getScorecardHistory.rejected, (state, action) => {
            state.scorecardDetailsLoading = false;
            state.scorecardDetailsError = true;
        });
        builder.addCase(getDigitalFootprints.fulfilled, (state, action) => {
            if (action?.payload?.response?.status === 500 || action?.payload?.response?.status === 404 || action?.payload === undefined) {
                state.scorecardDetailsError = true;
                state.scorecardDetailsLoading = false;
            } else {
                state.scorecardDigitalFootprints = action?.payload?.data;
                state.scorecardDetailsLoading = false;
                state.scorecardDetailsError = false;
            }
        });
        builder.addCase(getDigitalFootprints.pending, (state) => {
            state.scorecardDetailsLoading = true;
            state.scorecardDetailsError = false;
        });
        builder.addCase(getDigitalFootprints.rejected, (state, action) => {
            state.scorecardDetailsLoading = false;
            state.scorecardDetailsError = true;
        });

        builder.addCase(hasFraudScorecard.fulfilled, (state, action) => {
            if (action?.payload?.response?.status === 500 || action?.payload?.response?.status === 404 || action?.payload === undefined) {
                state.hasFraudScorecardLoading = false;
            } else {
                state.hasFraudScorecardDetail = action?.payload?.data?.exist;
                state.hasFraudScorecardLoading = false;
            }
        });
        builder.addCase(hasFraudScorecard.pending, (state) => {
            state.hasFraudScorecardLoading = true;
        });
        builder.addCase(hasFraudScorecard.rejected, (state, action) => {
            state.hasFraudScorecardLoading = false;
        });

        builder.addCase(hasSSLScorecard.fulfilled, (state, action) => {
            if (action?.payload?.response?.status === 500 || action?.payload?.response?.status === 404 || action?.payload === undefined) {
                state.hasSSLScorecardLoading = false;
            } else {
                state.hasSSLScorecardDetail = action?.payload?.data?.exist;
                state.hasSSLScorecardLoading = false;
            }
        });
        builder.addCase(hasSSLScorecard.pending, (state) => {
            state.hasSSLScorecardLoading = true;
        });
        builder.addCase(hasSSLScorecard.rejected, (state, action) => {
            state.hasSSLScorecardLoading = false;
        });

        builder.addCase(hasDehashedScorecard.fulfilled, (state, action) => {
            if (action?.payload?.response?.status === 500 || action?.payload?.response?.status === 404 || action?.payload === undefined) {
                state.hasDehashedScorecardLoading = false;
            } else {
                state.hasDehashedScorecardDetail = action?.payload?.data?.exist;
                state.hasDehashedScorecardLoading = false;
            }
        });
        builder.addCase(hasDehashedScorecard.pending, (state) => {
            state.hasDehashedScorecardLoading = true;
        });
        builder.addCase(hasDehashedScorecard.rejected, (state, action) => {
            state.hasDehashedScorecardLoading = false;
        });

        builder.addCase(hasDarkWebScorecard.fulfilled, (state, action) => {
            if (action?.payload?.response?.status === 500 || action?.payload?.response?.status === 404 || action?.payload === undefined) {
                state.hasDarkWebScorecardLoading = false;
            } else {
                state.hasDarkWebScorecardDetail = action?.payload?.data?.exist;
                state.hasDarkWebScorecardLoading = false;
            }
        });
        builder.addCase(hasDarkWebScorecard.pending, (state) => {
            state.hasDarkWebScorecardLoading = true;
        });
        builder.addCase(hasDarkWebScorecard.rejected, (state) => {
            state.hasDarkWebScorecardLoading = false;
        });

        builder.addCase(startThirdPartyDomainScan.fulfilled, (state, action) => {
            if (action?.payload?.message) {
                state.currentStep = 2;
                state.createStatus = {
                    message: action?.payload?.response?.data?.error,
                    error: true,
                    code: action?.payload?.response?.data?.status
                }
                state.thirdPartyScanAssetLoading = false;
            } else {
                const data = action?.payload?.data;
                state.currentStep = 2;
                if (data?.length > 0) {
                    state.notRegisteredThirdPartyAssets = data;
                }
            }
            state.thirdPartyScanAssetLoading = false;
        });
        builder.addCase(startThirdPartyDomainScan.rejected, (state, action) => {
            state.currentStep = 2;
            state.createStatus = {
                message: action.payload?.response?.data?.error,
                error: true,
            }
            state.thirdPartyScanAssetLoading = false;
        });
        builder.addCase(startThirdPartyDomainScan.pending, (state) => {
            state.currentStep = 1;
            state.createStatus = {
                message: '',
                error: false,
            }
            state.thirdPartyScanAssetLoading = true;
        });

        builder.addCase(getMultipleSelectedScorecards.fulfilled, (state, action) => {
            if (action?.payload?.response?.status === 500 || action?.payload?.response?.status === 404 || action?.payload === undefined) {
                state.thirdPartyGetError = true;
                state.thirdPartyGetLoading = false;
            } else {
                state.thirdPartyGetAll = action?.payload?.data;
                state.thirdPartyGetLoading = false;
                state.thirdPartyGetError = false;
            }
        });
        builder.addCase(getMultipleSelectedScorecards.pending, (state) => {
            state.thirdPartyGetLoading = true;
            state.thirdPartyGetError = false;
        });
        builder.addCase(getMultipleSelectedScorecards.rejected, (state, action) => {
            state.thirdPartyGetLoading = false;
            state.thirdPartyGetError = true;
        });

        builder.addCase(deleteMultipleSelectedScorecards.fulfilled, (state, action) => {
            if (action.payload?.status === 200 || action?.payload?.status === 204) {
                state.thirdPartyDeleteError = false;
                state.thirdPartyDeleteLoading = false;
            } else {
                state.thirdPartyDeleteLoading = false;
                state.thirdPartyDeleteError = true;
            }
        });
        builder.addCase(deleteMultipleSelectedScorecards.pending, (state) => {
            state.thirdPartyDeleteLoading = true;
            state.thirdPartyDeleteError = false;
        });
        builder.addCase(deleteMultipleSelectedScorecards.rejected, (state, action) => {
            state.thirdPartyDeleteLoading = false;
            state.thirdPartyDeleteError = true;
        });

        builder.addCase(addMultipleSelectedScorecards.fulfilled, (state, action) => {
            if (action?.payload?.response?.status === 500 || action?.payload?.response?.status === 404 || action?.payload === undefined) {
                state.thirdPartyAddError = true;
                state.thirdPartyAddLoading = false;
            } else {
                state.thirdPartyAddLoading = false;
                state.thirdPartyAddError = false;
            }
        });
        builder.addCase(addMultipleSelectedScorecards.pending, (state) => {
            state.thirdPartyAddLoading = true;
            state.thirdPartyAddError = false;
        });
        builder.addCase(addMultipleSelectedScorecards.rejected, (state, action) => {
            state.thirdPartyAddLoading = false;
            state.thirdPartyAddError = true;
        });

        builder.addCase(getAllExternalScorecards.fulfilled, (state, action) => {
            if (action?.payload?.response?.status === 500 || action?.payload?.response?.status === 404 || action?.payload === undefined) {
                state.thirdPartyGetExternalError = true;
                state.thirdPartyGetExternalLoading = false;
            } else {
                state.thirdPartyGetAllExternal = action?.payload?.data;
                state.thirdPartyGetExternalLoading = false;
                state.thirdPartyGetExternalError = false;
            }
        });
        builder.addCase(getAllExternalScorecards.pending, (state) => {
            state.thirdPartyGetExternalLoading = true;
            state.thirdPartyGetExternalError = false;
        });
        builder.addCase(getAllExternalScorecards.rejected, (state, action) => {
            state.thirdPartyGetExternalLoading = false;
            state.thirdPartyGetExternalError = true;
        });

        builder.addCase(getThirdPartyAssessmentPageable.fulfilled, (state, action) => {
            if (action?.payload?.response?.status === 500 || action?.payload?.response?.status === 404 || action?.payload === undefined) {
                state.thirdPartyPageableError = true;
                state.thirdPartyPageableLoading = false;
            } else {
                state.thirdPartyPageableData = {
                    content: action?.payload?.content || [],
                    totalElements: action?.payload?.totalElements || 0,
                    totalPages: action?.payload?.totalPages || 0,
                    pageable: {
                        pageNumber: action?.payload?.pageable?.pageNumber || 0,
                        pageSize: action?.payload?.pageable?.pageSize || 10
                    }
                };
                state.thirdPartyPageableLoading = false;
                state.thirdPartyPageableError = false;
            }
        });
        builder.addCase(getThirdPartyAssessmentPageable.pending, (state) => {
            state.thirdPartyPageableLoading = true;
            state.thirdPartyPageableError = false;
        });
        builder.addCase(getThirdPartyAssessmentPageable.rejected, (state, action) => {
            state.thirdPartyPageableLoading = false;
            state.thirdPartyPageableError = true;
        });

        builder.addCase(addSelectedScorecardWithContact.fulfilled, (state, action) => {
            if (action?.payload?.response?.status === 500 || action?.payload?.response?.status === 404 || action?.payload === undefined) {
                state.thirdPartyAddContactError = true;
                state.thirdPartyAddContactLoading = false;
            } else {
                state.thirdPartyAddContactLoading = false;
                state.thirdPartyAddContactError = false;
            }
        });
        builder.addCase(addSelectedScorecardWithContact.pending, (state) => {
            state.thirdPartyAddContactLoading = true;
            state.thirdPartyAddContactError = false;
        });
        builder.addCase(addSelectedScorecardWithContact.rejected, (state, action) => {
            state.thirdPartyAddContactLoading = false;
            state.thirdPartyAddContactError = true;
        });

        builder.addCase(deleteSelectedScorecardContact.pending, (state) => {
            state.thirdPartyDeleteContactLoading = true;
            state.thirdPartyDeleteContactError = null;
        })
        builder.addCase(deleteSelectedScorecardContact.fulfilled, (state) => {
            state.thirdPartyDeleteContactLoading = false;
        })
        builder.addCase(deleteSelectedScorecardContact.rejected, (state, action) => {
            state.thirdPartyDeleteContactLoading = false;
            state.thirdPartyDeleteContactError = action.payload;
        })

        builder.addCase(sendThirdPartyMail.pending, (state) => {
            state.sendMailLoading = true;
            state.sendMailError = null;
        })
        builder.addCase(sendThirdPartyMail.fulfilled, (state, action) => {
            if (action?.payload?.response?.status === 500 || action?.payload?.response?.status === 404 || action?.payload === undefined) {
                state.sendMailError = true;
                state.sendMailLoading = false;
            } else {
                state.sendMailLoading = false;
                state.sendMailError = false;
            }
        })
        builder.addCase(sendThirdPartyMail.rejected, (state, action) => {
            state.sendMailLoading = false;
            state.sendMailError = action.payload;
        })

        builder.addCase(getExternalHistory.fulfilled, (state, action) => {
            if (action?.payload?.response?.status === 500 || action?.payload?.response?.status === 404 || action?.payload === undefined) {
                state.externalHistoryDetailsError = true;
                state.externalHistoryDetailsLoading = false;
            } else {
                state.externalHistoryDetails = action?.payload;
                state.externalHistoryDetailsLoading = false;
                state.externalHistoryDetailsError = false;
            }
        });
        builder.addCase(getExternalHistory.pending, (state) => {
            state.externalHistoryDetailsLoading = true;
            state.externalHistoryDetailsError = false;
        });
        builder.addCase(getExternalHistory.rejected, (state, action) => {
            state.externalHistoryDetailsLoading = false;
            state.externalHistoryDetailsError = true;
        });

        builder.addCase(sendInitialConfirmation.pending, (state) => {
            state.sendMailLoading = true;
            state.sendMailError = null;
        })
        builder.addCase(sendInitialConfirmation.fulfilled, (state, action) => {
            if (action?.payload?.response?.status === 500 || action?.payload?.response?.status === 404 || action?.payload === undefined) {
                state.sendMailError = true;
                state.sendMailLoading = false;
            } else {
                state.sendMailLoading = false;
                state.sendMailError = false;
            }
        })
        builder.addCase(sendInitialConfirmation.rejected, (state, action) => {
            state.sendMailLoading = false;
            state.sendMailError = action.payload;
        })

        builder.addCase(resendConfirmation.pending, (state) => {
            state.sendMailLoading = true;
            state.sendMailError = null;
        })
        builder.addCase(resendConfirmation.fulfilled, (state, action) => {
            if (action?.payload?.response?.status === 500 || action?.payload?.response?.status === 404 || action?.payload === undefined) {
                state.sendMailError = true;
                state.sendMailLoading = false;
            } else {
                state.sendMailLoading = false;
                state.sendMailError = false;
            }
        })
        builder.addCase(resendConfirmation.rejected, (state, action) => {
            state.sendMailLoading = false;
            state.sendMailError = action.payload;
        })

        builder.addCase(getIndustryBenchmark.pending, (state) => {
            state.industryBenchmarkLoading = true;
        })
        builder.addCase(getIndustryBenchmark.fulfilled, (state, action) => {
            state.industryBenchmarkLoading = false;
            state.industryBenchmark = action.payload || [];
        })
        builder.addCase(getIndustryBenchmark.rejected, (state) => {
            state.industryBenchmarkLoading = false;
        })

        builder
            .addCase(getConfirmationTemplate.pending, (state) => {
                state.confirmationTemplateLoading = true;
            })
            .addCase(getConfirmationTemplate.fulfilled, (state, action) => {
                state.confirmationTemplateLoading = false;
                state.confirmationTemplate = action.payload;
            })
            .addCase(getConfirmationTemplate.rejected, (state) => {
                state.confirmationTemplateLoading = false;
            })
            .addCase(saveConfirmationTemplate.pending, (state) => {
                state.confirmationTemplateSaveLoading = true;
            })
            .addCase(saveConfirmationTemplate.fulfilled, (state) => {
                state.confirmationTemplateSaveLoading = false;
            })
            .addCase(saveConfirmationTemplate.rejected, (state) => {
                state.confirmationTemplateSaveLoading = false;
            });
        }
});

export const {
    setStep,
    resetScanStatus,
    resetNotRegisteredThirdPartyAssets,
    setActiveDomainWithTab,
    clearActiveDomainState,
    setActiveTab,
} = scorecardSlice.actions;

export default scorecardSlice.reducer;